import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isSupportedLang, normalizeLang, setLang } from '../lib/i18n';
import { NotificationBell } from './SidebarNotifications';
import { api } from '../lib/api';
import { mcpCall } from '../lib/mcp';
import { moduleDisplayName } from '../lib/module-identity';
import { setUiContext } from '../lib/ui-context';
import { IDENTITY_ME_QUERY_KEY, type IdentityMe } from '../lib/identity-me';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeSwitcher, useAccountTheme } from './ThemeSwitcher';
import {
  Archive,
  BookOpen,
  Box,
  CalendarClock,
  ChevronRight,
  FileText,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  type LucideIcon,
  Network,
  Palette,
  PanelLeftClose,
  PanelLeftOpen,
  Pin,
  Plug,
  Puzzle,
  Search,
  Settings,
  ShieldCheck,
  UserCircle,
  Users,
  Wrench,
  Zap,
} from 'lucide-react';
import { CodableIcon } from './icons/CodableIcon';
import { getIcon } from '../lib/module-icons';
import ChatPanel from './ChatPanel';
import McpConnectModal from './McpConnectModal';
import PhoneShell from './PhoneShell';
import { LayoutModeFab, LayoutModeSwitcher } from './LayoutModeSwitcher';
import { NodeSwitch } from './NodeSwitch';
import { useUiLayout } from '../lib/ui-layout';
import { uiScope, uiScopeAllowsModule, uiScopeAllowsPath, uiScopeHome } from '../lib/ui-scope';
import { maturityToTier } from './ui/MaturityBadge';

// Ikony modulů: jedna mapa pro sidebar, start menu i Aplikace (lib/module-icons).

// Moduly se SKUTEČNOU UI stránkou (pages/modules/<name>.tsx) — modul bez ní
// nemá v menu co dělat, i kdyby deklaroval ui.* (Filip 2026-07-27).
const MODULE_PAGES = new Set(
  Object.keys(import.meta.glob('../pages/modules/*.tsx')).map((p) =>
    p.replace('../pages/modules/', '').replace('.tsx', '')
  ),
);

// ── Typy ──────────────────────────────────────────────────────────────────────

interface ModuleMeta {
  name: string;
  nav: boolean;
  maturity: string;
  core: boolean;
  /** Strojová identita + anglický familiar name (2026-08-25). */
  uuid?: string | null;
  canonical_name?: string | null;
  ui?: { label: string; icon: string; section: string } | null;
}

// ── Nav link ──────────────────────────────────────────────────────────────────

function NavItem({ to, icon: Icon, label, end, badge, collapsed }: {
  to: string;
  icon: LucideIcon;
  label: string;
  end?: boolean;
  badge?: string;
  collapsed?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={!!end}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `flex items-center ${collapsed ? 'justify-center py-2.5' : 'gap-3 py-2.5'} ${
          collapsed ? 'px-2' : 'px-3'
        } rounded-xl text-sm font-medium transition-all group ${
          isActive
            ? 'bg-plex/15 text-plex border border-plex/25 acm-active-glow'
            : 'text-gray-400 hover:text-gray-100 hover:bg-gray-800/50 border border-transparent'
        }`}
    >
      {({ isActive }) => (
        <>
          <Icon
            size={collapsed ? 22 : 15}
            strokeWidth={collapsed ? 1.75 : 2}
            className={isActive ? 'text-plex' : 'text-gray-400 group-hover:text-gray-200'}
          />
          {!collapsed && <span className='flex-1'>{label}</span>}
          {!collapsed && badge && (
            <span className='text-[9px] font-bold tracking-wider text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1 py-0.5 rounded'>
              {badge}
            </span>
          )}
          {!collapsed && isActive && <ChevronRight size={13} className='text-plex' />}
        </>
      )}
    </NavLink>
  );
}

// ── Připnuté panely (My Panel) — čteno z ui.prefs (per-entity) ─────────────────

function PinnedPanelsNav({ collapsed }: { collapsed: boolean }) {
  const { data } = useQuery({
    queryKey: ['ui-prefs-panels'],
    queryFn: () =>
      api.mcp<
        { value?: { panels?: Array<{ id: string; label: string; pinned?: boolean }> } | null }
      >(
        'ui.prefs_get',
        { key: 'panels' },
      ).then((r) => r?.value?.panels ?? []).catch(() => []),
    staleTime: 30_000,
  });
  const pinned = (data ?? []).filter((p) => p.pinned);
  if (pinned.length === 0) return null;
  return (
    <>
      {pinned.map((p) => (
        <NavItem
          key={p.id}
          to={`/start?panel=${encodeURIComponent(p.id)}`}
          icon={Pin}
          label={p.label}
          collapsed={collapsed}
        />
      ))}
    </>
  );
}

// ── Section header ────────────────────────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <div className='px-3 pt-4 pb-1'>
      <span className='text-[10px] font-semibold text-gray-600 uppercase tracking-widest'>
        {label}
      </span>
    </div>
  );
}

// ── Maturity → tier mapping ───────────────────────────────────────────────────

// Moduly s VLASTNÍM nav pinem (CORE_NAV nebo bespoke stránka) — vyloučit z
// generického modul-seznamu, ať se nezdvojí. POZOR: tohle je NAV DE-DUP, NE
// routing (ten jede z module.json `core`). Nepleť si to — proto oddělené.
// Drž synchronní s `module` fieldy v CORE_NAV níž.
const NAV_DEDICATED = new Set(['toolbox', 'tasks', 'wiki', 'docker', 'mesh', 'codable', 'backup']);

// Route odvozena JEN z module.json `core` (kontrakt UI identity 2026-07-24):
// core:true → /core/:name, jinak → /modules/:name. Dvě cesty, žádné hardcoded
// jméno, žádné dev/test štěpení dle maturity (byl to drift — 27 modulů
// deklarovalo core:true, ale UI znalo jen 4).
function moduleRoute(name: string, isCore: boolean): string {
  return isCore ? `/core/${name}` : `/modules/${name}`;
}

// Vidí uživatel modul? JEDEN zdroj pravdy pro nav i hledání (Filip 2026-07-24:
// „jakýkoli pokyn i UI se řídí tím, kdo ho pouští"). admin/wildcard = vše,
// jinak musí mít aspoň jeden scope daného modulu.
function canSeeModule(name: string, scopes: string[]): boolean {
  // Dedicated URL (CODABLE hub) ukazuje jen produkt + související moduly —
  // i adminovi. Není to oprávnění, je to rozsah té publikované plochy.
  const scope = uiScope();
  if (scope) return uiScopeAllowsModule(scope, name);
  if (scopes.includes('acm:admin') || scopes.includes('*:read')) return true;
  return scopes.some((s) => s.startsWith(name + ':'));
}

/** Nav položka: platformní (bez `module`) jen pro admin/operator/viewer. */
function canSeeNavEntry(
  entry: { module?: string; always?: boolean; to: string },
  scopes: string[],
  platform = false,
): boolean {
  const scope = uiScope();
  if (entry.always) return !scope || uiScopeAllowsPath(scope, entry.to);
  if (scope) return !!entry.module && uiScopeAllowsModule(scope, entry.module);
  if (!entry.module) return platform;
  return canSeeModule(entry.module, scopes);
}

// ── Nav search ────────────────────────────────────────────────────────────────

// label = fallback (CZ origin); nav.<key> je i18n klíč (viz locales)
interface StaticNavEntry {
  to: string;
  icon: LucideIcon;
  label: string;
  tkey: string;
  end?: boolean;
  /** Základní zákaznická cesta dostupná všem přihlášeným rolím. */
  always?: boolean;
  /** Položka reprezentuje modul → zobrazí se JEN se scope na něj (canSeeModule).
   *  Bez module = platformní položka (Dashboard, Panel, Moduly) viditelná všem. */
  module?: string;
}

const CORE_NAV: StaticNavEntry[] = [
  { to: '/start', icon: LayoutGrid, label: 'Start', tkey: 'start', end: true },
  { to: '/apps', icon: Users, label: 'Apps', tkey: 'apps', always: true },
  { to: '/core/dashboard', icon: LayoutDashboard, label: 'Dashboard', tkey: 'dashboard' },
  {
    to: '/modules/codable',
    icon: CodableIcon,
    label: 'Codable',
    tkey: 'codable',
    module: 'codable',
  },
  {
    to: '/core/mesh',
    icon: Network,
    label: 'Infrastructure',
    tkey: 'infrastructure',
    module: 'mesh',
  },
  { to: '/core/containers', icon: Box, label: 'Containers', tkey: 'containers', module: 'docker' },
  { to: '/core/toolbox', icon: Wrench, label: 'Toolbox', tkey: 'toolbox', module: 'toolbox' },
  { to: '/backup', icon: Archive, label: 'Backup', tkey: 'backup', module: 'backup' },
  { to: '/core/tasks', icon: CalendarClock, label: 'Tasks', tkey: 'tasks', module: 'tasks' },
  { to: '/core/wiki', icon: BookOpen, label: 'Wiki', tkey: 'wiki', always: true },
  { to: '/modules', icon: Layers, label: 'Modules', end: true, tkey: 'modules' },
];

const ADMIN_NAV: StaticNavEntry[] = [
  { to: '/identity', icon: ShieldCheck, label: 'Identity', tkey: 'identity' },
  { to: '/tools', icon: Wrench, label: 'MCP Tools', tkey: 'mcpTools' },
  { to: '/core/logy', icon: FileText, label: 'Logs', tkey: 'logs' },
];

// BOTTOM_NAV odstranen — Addon Store je v ADMIN_NAV, Design System je podminka na dev

// label = fallback (CZ origin); layout.toolboxSubs.<tkey> je i18n klíč (viz locales)
const TOOLBOX_SUBS = [
  {
    to: '/core/toolbox?tab=health',
    label: 'Health Monitor',
    tkey: 'healthMonitor',
    keywords: 'health monitor kontrola zdravi',
  },
  {
    to: '/core/toolbox?tab=security',
    label: 'Security Scanner',
    tkey: 'securityScanner',
    keywords: 'security scan bezpecnost',
  },
  {
    to: '/core/toolbox?tab=updates',
    label: 'System Updates',
    tkey: 'systemUpdates',
    keywords: 'update aktualizace git pull',
  },
  {
    to: '/core/toolbox?tab=backup',
    label: 'Backup & Snapshots',
    tkey: 'backupSnapshots',
    keywords: 'backup zaloha snapshot restore obnova',
  },
  {
    to: '/core/toolbox?tab=cleaner',
    label: 'Disk Cleaner',
    tkey: 'diskCleaner',
    keywords: 'cleaner cisteni disk prune',
  },
  {
    to: '/core/toolbox?tab=watchdog',
    label: 'Watchdog',
    tkey: 'watchdog',
    keywords: 'watchdog monitoring sluzby',
  },
  {
    to: '/core/toolbox?tab=crash-recovery',
    label: 'UI Crash Recovery',
    tkey: 'uiCrashRecovery',
    keywords: 'crash recovery error boundary ui chyba',
  },
  {
    to: '/modules/self-defence',
    label: 'Self-defence (IP blacklist/whitelist)',
    tkey: 'selfDefence',
    keywords: 'self defence ip blacklist whitelist security log brute force fail2ban firewall',
  },
];

function NavSearch({
  userScopes = [] as string[],
  platform = false,
}: {
  userScopes?: string[];
  platform?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['modules-nav'],
    queryFn: () => mcpCall<{ modules: ModuleMeta[] }>('acm-self.modules_list'),
    staleTime: 5 * 60_000,
  });

  const modules = data?.modules ?? [];

  const results = q.trim().length < 1 ? [] : (() => {
    const lq = q.toLowerCase();
    const allStatic = [...CORE_NAV, ...ADMIN_NAV];
    const staticHits = allStatic
      .filter((i) => canSeeNavEntry(i, userScopes, platform))
      .filter((i) => i.label.toLowerCase().includes(lq))
      .map((i) => ({ to: i.to, icon: i.icon, label: t(`nav.${i.tkey}`, i.label) }));
    const moduleHits = modules
      // Hledání ctí práva stejně jako menu — nenabízí modul, na který uživatel
      // nemá scope (jinak klik navigoval na stránku, co se hned zablokuje/zmizí).
      .filter((m) =>
        m.ui && canSeeModule(m.name, userScopes) && (
          m.name.toLowerCase().includes(lq) ||
          m.ui.label.toLowerCase().includes(lq) ||
          moduleDisplayName(m, (k, fb) => t(k, fb ?? '')).toLowerCase().includes(lq)
        )
      )
      .map((m) => {
        return {
          to: moduleRoute(m.name, m.core),
          icon: getIcon(m.ui!.icon),
          label: moduleDisplayName(m, (k, fb) => t(k, fb ?? '')),
        };
      });
    const subHits = (uiScope() ? [] : TOOLBOX_SUBS)
      .filter((s) => s.label.toLowerCase().includes(lq) || s.keywords.includes(lq))
      .map((s) => ({
        to: s.to,
        icon: getIcon('wrench'),
        label: t(`layout.toolboxSubs.${s.tkey}`, s.label),
      }));
    return [...staticHits, ...moduleHits, ...subHits].slice(0, 8);
  })();

  // Vybraný index drž v mezích při změně výsledků.
  useEffect(() => {
    setSel((s) => (results.length === 0 ? 0 : Math.min(s, results.length - 1)));
  }, [results.length]);

  function go(to: string) {
    navigate(to);
    setQ('');
    setOpen(false);
    setSel(0);
  }

  // Klávesnice: ↓/↑ pohyb, Enter otevři vybraný, Esc zavři.
  function onKey(e: ReactKeyboardEvent) {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel((s) => (s + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => (s - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const r = results[sel];
      if (r) go(r.to);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className='relative px-3 pb-2'>
      <div className='flex items-center gap-2 px-3 py-2 rounded-xl acm-glass border-gray-800/70'>
        <Search size={12} className='text-gray-500 flex-shrink-0' />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setSel(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={t('layout.searchPlaceholder', 'Hledat...')}
          className='flex-1 bg-transparent text-xs text-gray-100 placeholder-gray-500 outline-none'
        />
        {q && (
          <button
            onClick={() => {
              setQ('');
              setOpen(false);
            }}
            className='text-gray-500 hover:text-gray-100 text-xs'
          >
            x
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className='absolute left-3 right-3 top-full mt-1 z-50 acm-menu rounded-2xl overflow-hidden p-1 animate-fade-in'>
          {results.map((r, i) => {
            const Icon = r.icon;
            return (
              <button
                key={r.to}
                // onMouseDown + preventDefault: klik zabere spolehlivě (nezávodí
                // s blur/close, input neztratí fokus) — fix „klikneš a zmizí".
                onMouseDown={(e) => {
                  e.preventDefault();
                  go(r.to);
                }}
                onMouseEnter={() => setSel(i)}
                className={`acm-menu-item flex items-center gap-3 px-3 py-2.5 w-full text-left rounded-xl transition-colors ${
                  i === sel ? 'bg-plex/15 text-gray-100' : 'hover:bg-gray-800/50'
                }`}
              >
                <Icon
                  size={13}
                  className={`flex-shrink-0 ${i === sel ? 'text-plex' : 'text-gray-400'}`}
                />
                <span className='text-sm text-gray-100'>{r.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Dynamicke nav sekce ───────────────────────────────────────────────────────

// Sekce sidebaru = module.json `ui.section` (kontrakt UI identity 2026-07-24:
// „kam modul v UI patří" určuje deklarace, ne maturity). maturity zůstává jen
// jako badge (DEV/BETA) + hard block dev modulů na produkci (MR #126).
const NAV_SECTIONS = [
  { key: 'core', tkey: 'layout.moduleNav.coreSection', fallback: 'Core' },
  { key: 'services', tkey: 'layout.moduleNav.servicesSection', fallback: 'Services' },
  { key: 'infra', tkey: 'layout.moduleNav.infraSection', fallback: 'Infrastruktura' },
  { key: 'tools', tkey: 'layout.moduleNav.toolsSection', fallback: 'Tools' },
] as const;

const warnedSections = new Set<string>();

function DynamicModuleNav(
  { serverEnv, userScopes = [] as string[] }: {
    serverEnv: string;
    userScopes?: string[];
  },
) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['modules-nav'],
    queryFn: () => mcpCall<{ modules: ModuleMeta[] }>('acm-self.modules_list'),
    staleTime: 5 * 60_000,
  });
  // Per-user piny (ui_prefs 'nav_pins') řídí, které moduly jsou v menu.
  // Nahrazuje dřívější globální flag m.nav (ten byl sdílený všem). Moduly
  // se stálou položkou (CORE_NAV) sem nepatří (NAV_DEDICATED de-dup).
  const { data: pinsData } = useQuery({
    queryKey: ['ui-nav-pins'],
    queryFn: () =>
      api.mcpSession<{ value?: { pins?: string[] } | null }>('ui.prefs_get', { key: 'nav_pins' }),
    staleTime: 5 * 60_000,
  });
  const pins = pinsData?.value?.pins ?? [];
  const unpins = (pinsData?.value as { unpins?: string[] } | null | undefined)?.unpins ?? [];

  const visible = (data?.modules ?? []).filter((m) => {
    if (!m.ui) return false;
    if (!MODULE_PAGES.has(m.name)) return false; // bez reálné stránky nemá v menu co dělat
    if (NAV_DEDICATED.has(m.name)) return false;
    // Core moduly: v menu DEFAULTNĚ, ale jdou odepnout (unpins) — žádný
    // zašedlý nefunkční toggle. Ne-core dle pinů (Filip 2026-07-27).
    if (m.core ? unpins.includes(m.name) : !pins.includes(m.name)) return false;
    if (!canSeeModule(m.name, userScopes)) return false;
    // Na produkci nezobrazuj dev moduly (hard block, MR #126 design);
    // beta (test) se zobrazí s BETA badgem (MR #138 + #141).
    if (maturityToTier(m.maturity) === 'dev' && serverEnv === 'production') return false;
    return true;
  });

  if (visible.length === 0) return null;

  // Neznámá sekce nesmí tiše zmizet (dřív apps/monitoring/remote → modul
  // nikde). Warn + zařadit pod Nástroje, CI gate to zamítne už v MR.
  const known = new Set<string>(NAV_SECTIONS.map((s) => s.key));
  const sectionOf = (m: ModuleMeta) => {
    const s = m.ui!.section;
    if (known.has(s)) return s;
    if (!warnedSections.has(s)) {
      warnedSections.add(s);
      console.warn(
        `[nav] Modul '${m.name}' deklaruje neznámou ui.section '${s}' — fallback 'tools'`,
      );
    }
    return 'tools';
  };

  return (
    <>
      {NAV_SECTIONS.map(({ key, tkey, fallback }) => {
        const mods = visible.filter((m) => sectionOf(m) === key);
        if (mods.length === 0) return null;
        return (
          <div key={key}>
            <SectionHeader label={t(tkey, fallback)} />
            {mods.map((m) => {
              const tier = maturityToTier(m.maturity);
              return (
                <NavItem
                  key={m.name}
                  to={moduleRoute(m.name, m.core)}
                  icon={getIcon(m.ui!.icon)}
                  label={moduleDisplayName(m, (k, fb) => t(k, fb ?? ''))}
                  badge={tier === 'dev' ? 'DEV' : tier === 'test' ? 'BETA' : undefined}
                />
              );
            })}
          </div>
        );
      })}
    </>
  );
}

// ── Layout ────────────────────────────────────────────────────────────────────

// ── User badge (přihlášený uživatel + logout) ────────────────────────────────

function useIdentityMe() {
  return useQuery({
    queryKey: IDENTITY_ME_QUERY_KEY,
    queryFn: () => mcpCall<IdentityMe>('identity.me'),
    staleTime: 60_000,
    retry: false,
  });
}

function useUserInfo() {
  const { data } = useIdentityMe();
  const kind = data?.session?.kind ?? 'api_key';
  return {
    username: data?.identity?.label ?? '…',
    entityId: data?.identity?.id,
    authTypeRaw: kind === 'api_key' ? 'apikey' as const : kind,
  };
}

// authType = přeložený text jen pro ZOBRAZENÍ; logika (handleLogout apod.) musí vždy větvit
// na authTypeRaw, ne na tomto výstupu — jinak se větev rozbije při změně jazyka/překladu.
type TFunc = ReturnType<typeof useTranslation>['t'];

function authTypeLabel(t: TFunc, raw: 'keycloak' | 'local' | 'apikey'): string {
  if (raw === 'keycloak') return t('layout.authType.sso', 'Keycloak SSO');
  if (raw === 'local') return t('layout.authType.localPassword', 'lokální heslo');
  return t('layout.authType.apiKey', 'API key');
}

function UserAvatar({ username, size = 'sm' }: { username: string; size?: 'sm' | 'md' }) {
  const px = size === 'md' ? 'w-8 h-8 text-sm' : 'w-7 h-7 text-xs';
  return (
    <div
      className={`${px} rounded-full bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400 font-bold shrink-0`}
    >
      {username.charAt(0).toUpperCase()}
    </div>
  );
}

function UserBadge() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { username, authTypeRaw } = useUserInfo();
  const authType = authTypeLabel(t, authTypeRaw);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showConnect, setShowConnect] = useState(false);

  const handleLogout = useCallback(() => {
    if (authTypeRaw === 'keycloak') {
      setShowLogoutDialog(true);
    } else {
      window.location.href = '/auth/logout';
    }
  }, [authTypeRaw]);

  return (
    <div className='p-3 border-t border-gray-800/60'>
      <div className='flex items-center gap-2'>
        <button
          type='button'
          onClick={() => navigate('/profile')}
          className='flex items-center gap-2 flex-1 min-w-0 px-2 py-1.5 rounded-xl hover:bg-gray-800/50 transition-colors'
          title={t('nav.profile', 'Profil')}
        >
          <UserAvatar username={username} />
          <div className='min-w-0 text-left'>
            <div className='text-xs text-gray-100 font-medium truncate'>{username}</div>
            <div className='text-[10px] text-gray-500'>{authType}</div>
          </div>
        </button>
        {/* Zvonek ve stejném řádku / velikosti jako Plug a Logout */}
        <NotificationBell variant='inline' />
        {!uiScope() && (
          <button
            type='button'
            onClick={() => setShowConnect(true)}
            className='p-1.5 rounded-lg text-gray-500 hover:text-gray-100 hover:bg-gray-800/50 transition-colors shrink-0'
            title={t('connect.title', 'Připojit se k ACM přes MCP')}
          >
            <Plug size={14} />
          </button>
        )}
        <button
          type='button'
          onClick={handleLogout}
          className='p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-gray-800/50 transition-colors shrink-0'
          title={t('nav.logout', 'Odhlásit')}
        >
          <LogOut size={14} />
        </button>
      </div>

      {/* MCP connect modal — připojení celého ACM (osobní token dle oprávnění) */}
      {!uiScope() && showConnect && <McpConnectModal onClose={() => setShowConnect(false)} />}

      {/* Logout dialog */}
      {showLogoutDialog && <SsoLogoutDialog onClose={() => setShowLogoutDialog(false)} />}
    </div>
  );
}

function SsoLogoutDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  // Portal — backdrop-filter na sidebaru jinak uvězní fixed layout uvnitř lišty
  return createPortal(
    <div
      className='acm-overlay fixed inset-0 z-[90] flex items-center justify-center p-4'
      onClick={onClose}
    >
      <div
        className='acm-glass-strong rounded-2xl w-full max-w-sm p-6 animate-fade-in'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center gap-3 mb-4'>
          <div className='w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center'>
            <LogOut size={20} className='text-red-400' />
          </div>
          <div>
            <h3 className='text-sm font-semibold text-gray-100'>
              {t('layout.ssoLogout.title', 'Odhlášení')}
            </h3>
            <p className='text-[11px] text-gray-500'>
              {t('layout.ssoLogout.subtitle', 'Přihlášen přes Keycloak SSO')}
            </p>
          </div>
        </div>

        <div className='space-y-2 mb-5'>
          <button
            type='button'
            onClick={() => {
              window.location.href = '/auth/logout';
            }}
            className='w-full p-3 rounded-xl border border-gray-700/80 bg-gray-800/50 hover:bg-gray-800 text-left transition-colors'
          >
            <div className='text-sm text-gray-100 font-medium'>
              {t('layout.ssoLogout.confirmLocal', 'Odhlásit z tohoto rozhraní')}
            </div>
            <div className='text-[11px] text-gray-500 mt-0.5'>
              {t(
                'layout.ssoLogout.confirmLocalHint',
                'Zůstanete přihlášeni v ostatních službách (Supabase, Studio…)',
              )}
            </div>
          </button>
          <button
            type='button'
            onClick={() => {
              window.location.href = '/auth/logout?sso=1';
            }}
            className='w-full p-3 rounded-xl border border-red-500/25 bg-red-500/8 hover:bg-red-500/15 text-left transition-colors'
          >
            <div className='text-sm text-red-400 font-medium'>
              {t('layout.ssoLogout.confirmAll', 'Odhlásit všude (SSO)')}
            </div>
            <div className='text-[11px] text-gray-500 mt-0.5'>
              {t(
                'layout.ssoLogout.confirmAllHint',
                'Ukončí session v Keycloak — odhlásí ze všech služeb najednou',
              )}
            </div>
          </button>
        </div>

        <button
          type='button'
          onClick={onClose}
          className='w-full py-2 text-xs text-gray-500 hover:text-gray-300 transition-colors'
        >
          {t('layout.ssoLogout.cancel', 'Zrušit')}
        </button>
      </div>
    </div>,
    document.body,
  );
}

// ── Collapsed footer — Gemini-style rail: nastavení + avatar dole ────────────
//
// Menu JSOU portály na document.body (jako ThemeSwitcher / NotificationBell).
// Sidebar má backdrop-filter → stacking context; absolute left-full z-50
// zůstane pod main a „zmizí" (Filip: nevykreslí se navrchu).

type FooterMenuPos = { left: number; bottom: number; width: number };

function CollapsedFooter() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { username, authTypeRaw } = useUserInfo();
  const authType = authTypeLabel(t, authTypeRaw);
  const [menu, setMenu] = useState<'none' | 'settings' | 'user'>('none');
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [pos, setPos] = useState<FooterMenuPos | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const settingsBtnRef = useRef<HTMLButtonElement>(null);
  const userBtnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const placeMenu = useCallback((which: 'settings' | 'user') => {
    const el = which === 'settings' ? settingsBtnRef.current : userBtnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const width = 240; // w-60
    const gap = 8;
    let left = r.right + gap;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, r.left - gap - width);
    }
    const bottom = Math.max(8, window.innerHeight - r.bottom);
    setPos({ left, bottom, width });
  }, []);

  useLayoutEffect(() => {
    if (menu === 'none') {
      setPos(null);
      return;
    }
    placeMenu(menu);
    const onResize = () => placeMenu(menu);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [menu, placeMenu]);

  useEffect(() => {
    if (menu === 'none') return;
    function handler(e: MouseEvent) {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setMenu('none');
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menu]);

  const handleLogout = useCallback(() => {
    setMenu('none');
    if (authTypeRaw === 'keycloak') {
      setShowLogoutDialog(true);
    } else {
      window.location.href = '/auth/logout';
    }
  }, [authTypeRaw]);

  const portalMenu = menu !== 'none' && pos && typeof document !== 'undefined'
    ? createPortal(
      <div
        ref={menuRef}
        className='acm-menu fixed z-[200] rounded-2xl animate-fade-in shadow-2xl'
        style={{ left: pos.left, bottom: pos.bottom, width: pos.width }}
      >
        {menu === 'settings'
          ? (
            <div className='p-3 space-y-3'>
              <div className='text-[10px] font-semibold text-gray-500 uppercase tracking-widest'>
                {t('nav.server', 'Server')}
              </div>
              <NodeSwitch />
              <div className='border-t border-gray-800 pt-2 space-y-2'>
                <div className='text-[10px] font-semibold text-gray-500 uppercase tracking-widest'>
                  {t('nav.appearance', 'Vzhled')}
                </div>
                <div className='flex flex-wrap items-center gap-1.5'>
                  <ThemeSwitcher />
                  <LayoutModeSwitcher />
                </div>
                <LanguageSwitcher />
              </div>
            </div>
          )
          : (
            <div className='overflow-hidden p-1'>
              <div className='flex items-center gap-2.5 px-3 py-3 border-b border-gray-800'>
                <UserAvatar username={username} size='md' />
                <div className='min-w-0'>
                  <div className='text-xs text-gray-100 font-medium truncate'>{username}</div>
                  <div className='text-[10px] text-gray-500'>{authType}</div>
                </div>
              </div>
              <button
                type='button'
                onClick={() => {
                  setMenu('none');
                  navigate('/profile');
                }}
                className='acm-menu-item flex items-center gap-3 px-3 py-2.5 w-full text-left text-sm text-gray-300 rounded-xl hover:bg-gray-800/60 transition-colors'
              >
                <UserCircle size={15} className='text-gray-400' /> {t('nav.profile', 'Profil')}
              </button>
              {!uiScope() && (
                <button
                  type='button'
                  onClick={() => {
                    setMenu('none');
                    navigate('/profile#acm-link');
                  }}
                  className='acm-menu-item flex items-center gap-3 px-3 py-2.5 w-full text-left text-sm text-gray-300 rounded-xl hover:bg-gray-800/60 transition-colors'
                >
                  <Puzzle size={15} className='text-gray-400' /> {t('acmLink.title', 'ACMLink')}
                </button>
              )}
              {!uiScope() && (
                <button
                  type='button'
                  onClick={() => {
                    setMenu('none');
                    navigate('/profile#ide');
                  }}
                  className='acm-menu-item flex items-center gap-3 px-3 py-2.5 w-full text-left text-sm text-gray-300 rounded-xl hover:bg-gray-800/60 transition-colors'
                >
                  <Plug size={15} className='text-gray-400' /> {t('profile.ideTitle', 'IDE linky')}
                </button>
              )}
              <button
                type='button'
                onClick={handleLogout}
                className='acm-menu-item flex items-center gap-3 px-3 py-2.5 w-full text-left text-sm text-gray-300 rounded-xl hover:bg-gray-800/60 hover:text-red-400 transition-colors'
              >
                <LogOut size={15} className='text-gray-400' /> {t('nav.logout', 'Odhlásit')}
              </button>
            </div>
          )}
      </div>,
      document.body,
    )
    : null;

  return (
    <div
      ref={ref}
      className='relative z-[30] shrink-0 p-2 border-t border-gray-800/60 flex flex-col items-center gap-1 bg-[rgb(var(--acm-glass)/0.55)] backdrop-blur-md'
    >
      {/* Stejný rail jako Settings: 40×40, ikona 20 */}
      <NotificationBell variant='overlay' />
      <button
        ref={settingsBtnRef}
        type='button'
        onClick={() => setMenu((m) => (m === 'settings' ? 'none' : 'settings'))}
        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
          menu === 'settings'
            ? 'bg-gray-800 text-gray-100'
            : 'text-gray-400 hover:text-gray-100 hover:bg-gray-800/50'
        }`}
        title={t('nav.settings', 'Nastavení')}
      >
        <Settings size={20} strokeWidth={1.75} />
      </button>
      <button
        ref={userBtnRef}
        type='button'
        onClick={() => setMenu((m) => (m === 'user' ? 'none' : 'user'))}
        className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
          menu === 'user' ? 'bg-gray-800' : 'hover:bg-gray-800/50'
        }`}
        title={username}
      >
        <UserAvatar username={username} size='md' />
      </button>

      {portalMenu}
      {showLogoutDialog && <SsoLogoutDialog onClose={() => setShowLogoutDialog(false)} />}
    </div>
  );
}

export default function AppLayout() {
  const { t, i18n } = useTranslation();
  const routeLocation = useLocation();
  const scopedTo = uiScope();
  const scopeNavigate = useNavigate();
  const { isPhone } = useUiLayout();
  useEffect(() => {
    // Publikuj route/menu do UI context registru (agent/IDE ví, kde uživatel stojí)
    setUiContext({ route: routeLocation.pathname });
  }, [routeLocation.pathname]);
  useEffect(() => {
    // Dedicated URL drží uživatele u produktu — výchozí /core/dashboard
    // ani ručně zadaná cesta ho nesmí pustit do celého ACM.
    if (!scopedTo) return;
    if (uiScopeAllowsPath(scopedTo, routeLocation.pathname)) return;
    scopeNavigate(uiScopeHome(scopedTo, isPhone), { replace: true });
  }, [scopedTo, routeLocation.pathname, scopeNavigate, isPhone]);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('acm_sidebar_collapsed') === '1';
  });
  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('acm_sidebar_collapsed', next ? '1' : '0');
      }
      return next;
    });
  }, []);
  const { data: serverInfo } = useQuery({
    queryKey: ['server-info'],
    queryFn: () =>
      mcpCall<
        { env: string; label: string | null; hostname: string; ui_build?: string; version?: string }
      >(
        'acm-self.ui_context',
      ),
    staleTime: 60_000,
    refetchInterval: 120_000, // periodicky, ať dlouho otevřený tab pozná nový build
    refetchOnWindowFocus: true,
  });
  const serverEnv = serverInfo?.env ?? 'dev';
  const isDev = serverEnv === 'dev';

  // Pouze presentation capability mapa. Autoritativní ACL znovu vynucuje
  // proxy při každém tool callu; browser scopes ani identitu netvrdí.
  const { data: meData } = useIdentityMe();
  const userScopes = meData?.capabilities?.is_admin
    ? ['acm:admin']
    : meData?.capabilities?.all_modules
    ? ['*:read']
    : (meData?.capabilities?.modules ?? []).map((name) => `${name}:read`);
  const platformNav = meData?.capabilities?.platform === true ||
    meData?.capabilities?.is_admin === true ||
    meData?.capabilities?.all_modules === true;

  // Jazyk per-user: po loginu načti z účtu (ui_prefs 'lang') a přepni, pokud se
  // liší od aktuálního (localStorage dá rychlý start, tohle sync napříč zařízeními).
  const { data: langPref } = useQuery({
    queryKey: ['ui-lang'],
    queryFn: () =>
      api.mcpSession<{ value?: { lang?: string } | null }>('ui.prefs_get', { key: 'lang' }),
    staleTime: 5 * 60_000,
    retry: false,
  });
  useEffect(() => {
    const l = langPref?.value?.lang;
    if (isSupportedLang(l) && normalizeLang(i18n.language || 'cs') !== l) {
      setLang(l);
    }
  }, [langPref, i18n.language]);

  // Theme per-user (ui.prefs 'theme') — localStorage cache v initTheme()
  useAccountTheme();

  // Metro phone shell (Windows Phone) — auto ≤768px nebo vynucený režim
  if (isPhone) {
    return <PhoneShell />;
  }

  return (
    <div className='h-screen acm-shell flex overflow-hidden'>
      <aside
        className={`${
          collapsed ? 'w-14' : 'w-[17rem]'
        } acm-sidebar relative z-20 flex flex-col flex-shrink-0 h-screen transition-all duration-150`}
      >
        <div className={`${collapsed ? 'p-3' : 'p-6'} border-b border-gray-800/60 relative`}>
          {!collapsed
            ? (
              <>
                {uiScope() === 'codable'
                  ? (
                    <>
                      <div className='flex items-center gap-2 font-bold text-xs uppercase tracking-widest mb-1'>
                        <Zap size={13} className='acm-brand-icon fill-current' />
                        <span className='acm-brand'>Codable</span>
                      </div>
                      <h1 className='text-xl font-extrabold text-gray-100 tracking-tight'>
                        {t('nav.codable', 'Codable')}
                      </h1>
                      <p className='text-[10px] text-gray-500 mt-0.5'>
                        {t('layout.codableTagline', 'Projekty a workspace')}
                      </p>
                    </>
                  )
                  : (
                    <>
                      {
                        /* Wordmark nese značku sám — nadřazený text nad ním byl
                          jen šum a verze u něj nesedla účařím ani řezem
                          (Filip 2026-09-06). Verze proto patří do popisku. */
                      }
                      <div
                        className='acm-logo-img h-7 w-[87px]'
                        role='img'
                        aria-label='ACM'
                      />
                      <p className='text-[10px] text-gray-500 mt-1.5'>
                        {t('layout.brandTagline', 'Agent Control Manager')}
                        <span className='text-gray-600'>
                          {' · v'}
                          {serverInfo?.version ?? '…'}
                        </span>
                      </p>
                    </>
                  )}
              </>
            )
            : (
              <div className='flex justify-center'>
                {uiScope() === 'codable'
                  ? <Zap size={20} className='acm-brand-icon fill-current' />
                  : <div className='acm-mark-img h-6 w-6' role='img' aria-label='ACM' />}
              </div>
            )}
          <button
            onClick={toggleCollapsed}
            className='absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full acm-glass-strong text-gray-500 hover:text-plex hover:border-plex/40 flex items-center justify-center shadow-glow transition-colors'
            title={collapsed
              ? t('layout.expandMenu', 'Rozbalit menu')
              : t('layout.collapseMenu', 'Sbalit menu')}
          >
            {collapsed ? <PanelLeftOpen size={12} /> : <PanelLeftClose size={12} />}
          </button>
        </div>

        <nav className={`flex-1 ${collapsed ? 'p-2' : 'p-3'} space-y-0.5 overflow-y-auto`}>
          {collapsed
            ? (
              !uiScope() && (
                <button
                  onClick={toggleCollapsed}
                  className='flex items-center justify-center py-2.5 px-2 w-full rounded-xl text-gray-400 hover:text-white hover:bg-gray-800/50 transition-all'
                  title={t('nav.search', 'Hledat')}
                >
                  <Search size={22} strokeWidth={1.75} />
                </button>
              )
            )
            : !uiScope() && <NavSearch userScopes={userScopes} platform={platformNav} />}
          {
            /* CORE — platformní položky všem; modulové jen se scope (kontrakt:
              „jakýkoli pokyn i UI se řídí tím, kdo ho pouští") */
          }
          {CORE_NAV
            .filter((i) => canSeeNavEntry(i, userScopes, platformNav))
            .map(({ to, icon, label, end, tkey }) => (
              <NavItem
                key={to}
                to={to}
                icon={icon}
                label={t(`nav.${tkey}`, label)}
                end={end}
                collapsed={collapsed}
              />
            ))}
          {/* Připnuté panely (My Panel) — ne na dedicated produktové URL */}
          {platformNav && !uiScope() && <PinnedPanelsNav collapsed={collapsed} />}
          {/* ADMIN — jen pokud má admin scope */}
          {platformNav &&
            ADMIN_NAV.filter(() => !uiScope()).map(({ to, icon, label, end, tkey }) => (
              <NavItem
                key={to}
                to={to}
                icon={icon}
                label={t(`nav.${tkey}`, label)}
                end={end}
                collapsed={collapsed}
              />
            ))}
          {/* Design System — core dev nastroj, ne modul */}
          {isDev && platformNav && !uiScope() && (
            <NavItem
              to='/core/design'
              icon={Palette}
              label={t('layout.designSystem', 'Design System')}
              badge='DEV'
              collapsed={collapsed}
            />
          )}

          {
            /* MODULES — připnuté moduly grupované dle ui.section, jen když
              rozbaleno (dynamické headers by nedávaly smysl bez labelů) */
          }
          {!collapsed && <DynamicModuleNav serverEnv={serverEnv} userScopes={userScopes} />}
        </nav>

        {collapsed ? <CollapsedFooter /> : (
          <>
            {/* Zvonek je v UserBadge (stejný řádek jako Plug/Logout) */}
            <UserBadge />
            <div className='p-3 border-t border-gray-700/50 space-y-2.5'>
              <NodeSwitch />
              <div className='px-1.5 space-y-1.5'>
                <div className='flex flex-wrap items-center gap-1.5'>
                  <ThemeSwitcher />
                  <LayoutModeSwitcher />
                </div>
                <LanguageSwitcher />
              </div>
            </div>
          </>
        )}
      </aside>

      <main className='flex-1 overflow-y-auto'>
        {
          /* Banner nové verze ZRUŠEN — oznámení o nové verzi žije VÝHRADNĚ
            v oznamovacím centru (NotificationBell), Filip 2026-07-24. */
        }
        <div className='p-8 min-h-full'>
          <Outlet />
        </div>
      </main>

      {!uiScope() && <ChatPanel />}
      {/* Mobil: trvalý FAB Desktop ↔ Telefon (i když je vynucený desktop na úzkém viewportu) */}
      <LayoutModeFab />
    </div>
  );
}
